import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import EmergencyBar from "@/components/EmergencyBar";
import MobileBookBar from "@/components/MobileBookBar";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import { SiteConfigProvider } from "@/lib/siteConfig";
import { getBasePath } from "@/lib/buildSnapshot";
import { AnalyticsNavigation } from "@/components/AnalyticsNavigation";

// Homepage is eager (first-paint critical).
import HomePage from "@/pages/Home";
import { LanguageProvider } from "@/i18n/context";
import { languageOf } from "@/i18n/routes";
const HindiProvider = lazy(() => import("@/i18n/HindiProvider"));

// Route-level code-splitting: everything below is loaded on demand,
// shrinking the initial JS bundle by ~55% and improving mobile LCP/TTI.
const DestinationsPage = lazy(() => import("@/pages/Destinations"));
const AboutPage = lazy(() => import("@/pages/About"));
const FleetPage = lazy(() => import("@/pages/Fleet"));
const ServicesPage = lazy(() => import("@/pages/Services"));
const ContactPage = lazy(() => import("@/pages/Contact"));
const VehicleDetailPage = lazy(() => import("@/pages/VehicleDetail"));
const LocationDetailPage = lazy(() => import("@/pages/LocationDetail"));
const RouteDetailPage = lazy(() => import("@/pages/RouteDetail"));
const ServiceLocationsPage = lazy(() => import("@/pages/ServiceLocations"));
const TempoTravellerPage = lazy(() => import("@/pages/TempoTraveller"));
const TempoTravellerCityPage = lazy(() => import("@/pages/TempoTravellerCityPage"));
const VehicleCompare = lazy(() => import("@/pages/VehicleCompare"));
const OutstationPage = lazy(() => import("@/pages/Outstation"));
const AirportTransferPage = lazy(() => import("@/pages/AirportTransfer"));
const RailwayTransferPage = lazy(() => import("@/pages/RailwayTransfer"));
const PolicyPage = lazy(() => import("@/pages/Policy"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));

// Admin — separate chunk, only pulled when navigating to /admin/*.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminPricing = lazy(() => import("@/pages/admin/AdminPricing"));

import { VEHICLES } from "@/data/vehicles";
import { LOCATIONS } from "@/data/locations";
import { POPULAR_ROUTES } from "@/data/routes";
import { TEMPO_CITY_SLUGS } from "@/data/cityContent";

const RouteFallback = () => (
    <div className="container-x flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-gold border-t-transparent" />
    </div>
);

function LanguageBoundary({ language, children }) {
    const { pathname } = useLocation();
    const Provider = language === "hi" ? HindiProvider : LanguageProvider;
    return <Provider pathname={pathname}>{children}</Provider>;
}

function PublicChrome({ children }) {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");
    const informationOnly = pathname === "/destinations" || pathname.startsWith("/destinations/");
    if (isAdmin) return children;
    return (
        <>
            <EmergencyBar informationOnly={informationOnly} />
            <Header informationOnly={informationOnly} />
            <main className="min-h-[60vh] pt-[92px] md:pt-[112px]">{children}</main>
            <Footer informationOnly={informationOnly} />
            {!informationOnly && <FloatingButtons />}
            {!informationOnly && <MobileBookBar />}
        </>
    );
}

export default function App({ Router = BrowserRouter, routerProps = {} }) {
    const rawLocation = routerProps.location;
    const requestPath = (typeof rawLocation === "string" ? rawLocation : rawLocation?.pathname) || (typeof window !== "undefined" ? window.location.pathname : "/");
    const basePath = getBasePath();
    const localPath = basePath && requestPath.startsWith(basePath) ? requestPath.slice(basePath.length) || "/" : requestPath;
    const language = languageOf(localPath);
    // Canonical Hindi root links include the slash; retain legacy /hi compatibility.
    const routerBasePath = basePath + (language === "hi" ? (localPath === "/hi" ? "/hi" : "/hi/") : "");
    return (
        <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <SiteConfigProvider>
                <div className="App">
                    <Router basename={routerBasePath || undefined} {...routerProps}>
                        <LanguageBoundary language={language}>
                        <AnalyticsNavigation />
                        <ScrollToTop />
                        <PublicChrome>
                            <ErrorBoundary>
                                <Suspense fallback={<RouteFallback />}>
                                <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/fleet" element={<FleetPage />} />
                            <Route path="/services" element={<ServicesPage />} />
                            <Route path="/tempo-traveller-rental" element={<TempoTravellerPage />} />
                            <Route path="/compare" element={<VehicleCompare />} />
                            <Route path="/outstation" element={<OutstationPage />} />
                            <Route path="/airport-transfer" element={<AirportTransferPage />} />
                            <Route path="/railway-transfer" element={<RailwayTransferPage />} />
                            <Route path="/service-locations" element={<ServiceLocationsPage />} />
                            <Route path="/destinations/*" element={<DestinationsPage />} />
                            <Route path="/contact" element={<ContactPage />} />

                            <Route path="/privacy-policy" element={<PolicyPage />} />
                            <Route path="/terms" element={<PolicyPage />} />
                            <Route path="/refund-policy" element={<PolicyPage />} />
                            <Route path="/cancellation-policy" element={<PolicyPage />} />

                            {LOCATIONS.map((l) => (
                                <Route key={`loc-${l.slug}`} path={`/taxi-service-${l.slug}`} element={<LocationDetailPage />} />
                            ))}
                            {TEMPO_CITY_SLUGS.map((s) => (
                                <Route
                                    key={`tempo-${s}`}
                                    path={`/tempo-traveller-rental-${s}`}
                                    element={<TempoTravellerCityPage />}
                                />
                            ))}
                            {POPULAR_ROUTES.map((r) => (
                                <Route key={`route-${r.slug}`} path={`/${r.slug}`} element={<RouteDetailPage />} />
                            ))}
                            {VEHICLES.map((v) => (
                                <Route key={`veh-${v.slug}`} path={`/${v.slug}`} element={<VehicleDetailPage />} />
                            ))}

                            <Route path="/taxi-service-allahabad" element={<Navigate to="/taxi-service-prayagraj" replace />} />
<Route path="/tempo-traveller-rental-allahabad" element={<Navigate to="/tempo-traveller-rental-prayagraj" replace />} />
<Route path="/taxi-service-chitrakoot-dham" element={<Navigate to="/taxi-service-chitrakoot" replace />} />
<Route path="/tempo-traveller-rental-chitrakoot-dham" element={<Navigate to="/tempo-traveller-rental-chitrakoot" replace />} />
                            {/* Admin */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="bookings" element={<AdminBookings />} />
                                <Route path="contacts" element={<AdminContacts />} />
                                <Route path="content" element={<AdminContent />} />
                                <Route path="pricing" element={<AdminPricing />} />
                                <Route path="settings" element={<AdminSettings />} />
                            </Route>

                            {/*
                              * Legacy URL redirects.
                              *
                              * Emergent's static hosting cannot emit HTTP 301,
                              * so per-URL redirects live in two layers:
                              *   1. A static SEO shell in build/{path}/index.html
                              *      with a zero-second <meta http-equiv="refresh">
                              *      + canonical to the target (see
                              *      scripts/build-seo-shells.js LEGACY_REDIRECTS).
                              *   2. A React <Navigate> here so that SPA users
                              *      arriving via client-side navigation are
                              *      also sent to the equivalent live page.
                              * Both layers redirect to the same destination.
                              * Do NOT combine with noindex or a 404 render —
                              * this is a redirect response, not an error.
                              */}
                            <Route
                                path="/vvpss-blog/f/diamond-city-wildlife-haven-your-guide-on-how-to-reach-panna"
                                element={<Navigate to="/taxi-service-panna" replace />}
                            />
                            <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                                </Suspense>
                            </ErrorBoundary>
                        </PublicChrome>
                        <Toaster
                            position="top-center"
                            richColors
                            toastOptions={{ style: { fontFamily: "Manrope, sans-serif" } }}
                        />
                        </LanguageBoundary>
                    </Router>
                </div>
            </SiteConfigProvider>
            </Suspense>
        </ErrorBoundary>
    );
}
